<template>
  <div class="filter-detail" :style="positionStyle">
    <!-- Header (не менял) -->
    <div class="detail-head">
      <h3>{{ filterLabel || filterKey }}</h3>
      <button class="close" type="button" @click="$emit('close')">✕</button>
    </div>

    <!-- Scrollable body: search + options.
         Это единственная прокручиваемая область — кнопки вынесены ниже и остаются видимыми -->
    <div class="detail-body">
      <input v-model="q" placeholder="Поиск..." class="search" />
      <ul>
        <li v-for="opt in filteredOptions" :key="opt" class="opt-row">
          <span class="opt-text">{{ opt }}</span>
          <label class="opt-checkbox">
            <input type="checkbox" :value="opt" :checked="isSelected(opt)" @change="toggle(opt, $event)" />
          </label>
        </li>
      </ul>
    </div>

    <!-- Footer с действиями — вне прокручиваемой области, всегда виден -->
    <div class="detail-footer">
      <div class="actions">
        <button type="button" class="apply" @click="apply">Применить</button>
        <button type="button" class="clear" @click="clear">Сброс</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FilterDetailPanel',
  props: {
    filterKey: { type: String, required: true },
    filterLabel: { type: String, default: null },
    options: { type: Array, default: () => [] },
    position: { type: Object, default: null },
    initialSelected: { type: Array, default: () => [] } // initial selection from draft
  },
  emits: ['apply','clear','close'],
  data() {
    return { q: '', selected: Array.isArray(this.initialSelected) ? [...this.initialSelected] : [] }
  },
  watch: {
    initialSelected(newV) {
      // keep local selection in sync when parent updates draftFilters
      this.selected = Array.isArray(newV) ? [...newV] : []
    },
    options() {
      // if options changed — reset search
      this.q = ''
    }
  },
  computed: {
    filteredOptions() {
      if (!this.q) return this.options
      const qq = this.q.toLowerCase()
      return this.options.filter(o => String(o).toLowerCase().includes(qq))
    },
    /* positionStyle: small improvement — expose CSS variable --fd-top together with top:var(--fd-top)
       Это позволяет в CSS корректно вычислять max-height и гарантировать пространство до футера */
    positionStyle() {
      if (!this.position) {
        return {
          position: 'absolute',
          right: '24px',
          top: 'var(--fd-top)',
          '--fd-top': '120px',
          zIndex: 1250,
          width: '360px'
        }
      }
      const style = Object.assign({}, this.position)
      const explicitTop = this.position.top !== undefined ? this.position.top : '120px'
      style['--fd-top'] = String(explicitTop)
      style.top = 'var(--fd-top)'
      style.position = style.position || 'absolute'
      style.zIndex = style.zIndex || 1250
      return style
    }
  },
  methods: {
    isSelected(opt) { return Array.isArray(this.selected) && this.selected.indexOf(opt) !== -1 },
    toggle(opt, evt) {
      const checked = !!evt.target.checked
      const arr = Array.isArray(this.selected) ? [...this.selected] : []
      if (checked) {
        if (!arr.includes(opt)) arr.push(opt)
      } else {
        const idx = arr.indexOf(opt); if (idx !== -1) arr.splice(idx, 1)
      }
      this.selected = arr
      // We DO NOT auto-emit apply — we keep it in draft until user presses Apply
    },
    apply() {
      this.$emit('apply', { key: this.filterKey, value: Array.isArray(this.selected) ? this.selected : [] })
      this.$emit('close')
    },
    clear() {
      this.selected = []
      this.$emit('clear', { key: this.filterKey })
    }
  }
}
</script>

<style scoped>
/* ---------- Основная идея -------------
   - Внешний .filter-detail — flex-контейнер column.
   - .detail-body — flex:1 и overflow:auto (прокрутка только в теле).
   - .detail-footer — flex:0 0 auto; всегда находится внизу панeли и виден.
   - max-height рассчитывается через --fd-top (см. positionStyle), + отступ перед футером сайта.
   -------------------------------------- */

.filter-detail {
  /* display:flex + column — ключ к фиксированному футеру */
  display: flex;
  flex-direction: column;

  position: absolute; /* position управляется через :style */
  right: 24px;
  width: min(360px, calc(100% - 48px));
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
  padding: 12px;
  z-index: 1250;
  color: #222;

  /* гарантируем, что панель не займёт экран целиком и оставит место до основного футера сайта */
  max-height: calc(100vh - var(--fd-top, 120px) - (var(--map-footer-space, 120px) + 12px));
}

/* Header (как было) */
.detail-head{ display:flex; align-items:center; justify-content:space-between; gap:8px }
.detail-head h3{ margin:0; font-size:18px; color:#222 }
.close{ background:transparent; border:none; cursor:pointer; font-size:18px }

/* Тело — прокручиваемая область. Это единственная область с overflow. */
.detail-body{
  /* растягиваем тело чтобы занять всё доступное пространство между header и footer */
  flex: 1 1 auto;
  min-height: 0; /* важно для flex + overflow корректности в некоторых браузерах */
  overflow: auto;
  padding-top: 8px;
}

/* Стили опций/поиска — без изменений (только вынесены в тело) */
.search{ width:100%; padding:8px; border-radius:8px; border:1px solid rgba(0,0,0,0.08); margin-bottom:8px; color:#222 }
ul{ list-style:none; padding:0; margin:0 }
.opt-row{ display:flex; align-items:center; justify-content:space-between; padding:8px 6px; border-bottom:1px solid rgba(0,0,0,0.04) }
.opt-text{ color:#222 }
.opt-checkbox input{ width:18px; height:18px }

/* Footer с кнопками — вне прокрутки и всегда виден */
.detail-footer{
  flex: 0 0 auto;
  border-top: 1px solid rgba(0,0,0,0.04);
  padding-top: 12px;
  margin-top: 8px;
  /* фон/подложка иногда помогает избежать визуальных артефактов при прозрачных тенях под списком */
  background: white;
}

/* Сами кнопки — стилистика не менялась (копия старых правил) */
.actions{ display:flex; gap:8px; justify-content:flex-end }
.apply{ background: var(--hp-button-color,#F6F6F6); border:none; padding:8px 10px; border-radius:8px; cursor:pointer; color:#222; font-weight:700 }
.clear{ background:transparent; border:1px solid rgba(0,0,0,0.06); padding:8px 10px; border-radius:8px; cursor:pointer; color:#222 }

/* На узких экранах немного корректируем позиционирование */
@media (max-width: 520px) {
  .filter-detail { right: 12px; left: 12px; width: auto }
}
</style>
